import React from 'react';
import type { EmailDomainResponse } from '@troia-v3/types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';
import { DomainStatusBadge } from './DomainStatusBadge';

/**
 * Card de domínio de e-mail (view "Cards" e mobile). Cards SEM checkbox (F34) —
 * seleção em massa existe somente na view Lista/Tabela. Menu ••• de ações.
 * Ícone sem foto = slate #64748b via AvatarFallback.
 */
export interface EmailDomainCardProps {
  domain: EmailDomainResponse;
  verifying?: boolean;
  onView?: (id: string) => void;
  onVerify?: (id: string) => void;
  onDelete?: (domain: EmailDomainResponse) => void;
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '—';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  } catch {
    return '—';
  }
};

const getVerifiedRecordsCount = (domain: EmailDomainResponse): string => {
  if (!domain.dnsRecords || domain.dnsRecords.length === 0) return '0/0';
  const verified = domain.dnsRecords.filter((r) => r.status === 'verified').length;
  return `${verified}/${domain.dnsRecords.length}`;
};

export const EmailDomainCard: React.FC<EmailDomainCardProps> = ({
  domain,
  verifying = false,
  onView,
  onVerify,
  onDelete,
}) => {
  const actions: CardAction[] = [];
  if (onView) actions.push({ label: 'Detalhes', icon: 'eye', onClick: () => onView(domain.id) });
  if (onVerify && domain.status !== 'verified') {
    actions.push({ label: 'Verificar', icon: 'refresh', onClick: () => onVerify(domain.id), disabled: verifying });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(domain), variant: 'danger' });
  }

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors cursor-pointer"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      onClick={() => onView?.(domain.id)}
      data-testid={`domain-card-${domain.id}`}
    >
      <div className="flex items-start gap-3">
        <Avatar className="w-11 h-11 flex-shrink-0" isSquircle>
          <AvatarFallback isSquircle>
            <Icon name="mail" className="w-5 h-5 text-white" />
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }} title={domain.domain}>
            {domain.domain}
          </h3>
          <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {domain.region || '—'}
          </p>
        </div>

        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1 -mt-1" onClick={(e) => e.stopPropagation()}>
            <CardActionsMenu actions={actions} size="sm" data-testid={`domain-actions-${domain.id}`} />
          </div>
        )}
      </div>

      {/* Verificação + capacidades */}
      <div className="mt-3 flex items-center flex-wrap gap-2">
        <DomainStatusBadge status={domain.status} />
        {domain.sendingEnabled && (
          <span className="inline-flex items-center gap-1 h-[23px] px-2.5 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
            <Icon name="send" className="w-3 h-3" /> Envio
          </span>
        )}
        {domain.receivingEnabled && (
          <span className="inline-flex items-center gap-1 h-[23px] px-2.5 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
            <Icon name="download" className="w-3 h-3" /> Recebimento
          </span>
        )}
      </div>

      {/* Footer: registros DNS + data */}
      <div
        className="mt-3 pt-3 flex items-center justify-between text-xs"
        style={{ borderTop: '1px solid var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
      >
        <span className="inline-flex items-center gap-1.5">
          <Icon name="check-circle" className="w-3.5 h-3.5" />
          Registros DNS: <span className="font-medium" style={{ color: 'var(--theme-card-text)' }}>{getVerifiedRecordsCount(domain)}</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Icon name="clock" className="w-3.5 h-3.5" />
          {formatDate(domain.createdAt)}
        </span>
      </div>
    </div>
  );
};

EmailDomainCard.displayName = 'EmailDomainCard';
